import { initStickySummaryBar } from '../../../js/stickySummaryBar.js';
import { readChildrenAgesFromParams } from '../../../js/offerSearchParams.js';
import {
  occupancyDiffersFromCard,
  shouldQuoteAfterOfferUrlHydrate,
} from '../../../js/offerUrlHydrate.js';

const i18n = (key, fallback) => {
  const bag = typeof window !== 'undefined' ? window.hskiI18n : null;
  const value = bag && typeof bag[key] === 'string' ? bag[key] : '';
  return value || fallback;
};

const ui = (msgid) => {
  const text = String(msgid ?? '');
  if (!text) return '';
  const bag = typeof window !== 'undefined' ? window.hskiI18n?.msg : null;
  const value = bag && typeof bag[text] === 'string' ? bag[text] : '';
  return value || text;
};

const uiFmt = (msgid, ...vars) => {
  let index = 0;
  return ui(msgid).replace(/%s/g, () => String(vars[index++] ?? ''));
};

const guestsLabel = (adults, children) => {
  const a = Number(adults) || 0;
  const c = Number(children) || 0;
  const adultWord = a === 1 ? i18n('adult_one', 'dorosły') : i18n('adult_many', 'dorosłych');
  const childWord = c === 1 ? i18n('child_one', 'dziecko') : i18n('child_many', 'dzieci');
  return `${a} ${adultWord}, ${c} ${childWord}`;
};

const nightsLabel = (nights) => {
  const n = Number(nights) || 0;
  if (n === 1) return i18n('night_one', '1 noc');
  if (n >= 2 && n <= 4) return i18n('night_few', '%d noce').replace('%d', String(n));
  return i18n('night_many', '%d nocy').replace('%d', String(n));
};

const nightsFromDates = (from, to) => {
  if (!from || !to) return 0;
  const a = new Date(`${from}T12:00:00`);
  const b = new Date(`${to}T12:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  const n = Math.round((b - a) / 86400000);
  return n > 0 ? n : 0;
};

const skipassDaysLabel = (nights) => {
  const days = Math.max(0, (Number(nights) || 0) - 1);
  if (days <= 0) return '';
  return days === 1
    ? i18n('day_one', '1 dzień')
    : i18n('day_many', '%d dni').replace('%d', String(days));
};

const resolveTermNights = (root) => {
  const option = getTermSelect(root)?.selectedOptions?.[0];
  const fromDates = nightsFromDates(option?.dataset.isoFrom || '', option?.dataset.isoTo || '');
  if (fromDates > 0) return fromDates;
  return Number(option?.dataset.nights || 0) || 0;
};


const ICON_CAL = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4.5" width="18" height="16.5" rx="2"></rect><path d="M3 9.5h18M8 2.5v4M16 2.5v4"></path></svg>';

const formatMoneyAmount = (amount, currency) => {
  const n = Math.round(Number(amount) || 0);
  const formatted = n.toLocaleString('pl-PL');
  const code = String(currency || '').toUpperCase();
  return `${formatted} ${code === 'PLN' ? 'zł' : (currency || '')}`.trim();
};

const splitPriceHint = (label) => {
  const text = String(label || '').trim();
  const match = text.match(/^(.*?)\s*\((.*)\)\s*$/);
  if (!match) return { title: text, hint: '' };
  return { title: match[1].trim(), hint: match[2].trim() };
};

const translateDayPhrase = (raw) => {
  const text = String(raw || '').trim();
  if (text === '1 dzień') return i18n('day_one', '1 dzień');
  const many = text.match(/^(\d+) dni$/);
  if (many) return i18n('day_many', '%d dni').replace('%d', many[1]);
  return text;
};

const translateStayHint = (hint) => {
  const text = String(hint || '').trim();
  if (!text) return '';
  const withDays = text.match(/^nocleg ze skipassem, (.+)$/);
  if (withDays) return uiFmt('nocleg ze skipassem, %s', translateDayPhrase(withDays[1]));
  return ui(text);
};

const appendSumLine = (parent, title, amountText, hint = '') => {
  const row = document.createElement('div');
  row.className = 'offer-page__sumln';
  const left = document.createElement('span');
  left.appendChild(document.createTextNode(title));
  if (hint) {
    left.appendChild(document.createTextNode(' '));
    const hintEl = document.createElement('span');
    hintEl.className = 'offer-page__sumhint';
    hintEl.textContent = `(${hint})`;
    left.appendChild(hintEl);
  }
  const right = document.createElement('span');
  right.textContent = amountText;
  row.appendChild(left);
  row.appendChild(right);
  parent.appendChild(row);
};

const applyOfferSummary = (root, quote) => {
  const sidebar = getOfferSidebar(root);
  const linesEl = sidebar?.querySelector('.js-offer-sum-lines');
  const totalEl = sidebar?.querySelector('.js-offer-sum-total');
  const noteEl = sidebar?.querySelector('.js-offer-sum-note');
  const roomRow = sidebar?.querySelector('.js-offer-sum-room');
  const roomLabel = sidebar?.querySelector('.js-offer-sum-room-label');
  if (!linesEl) return;

  const selected = root.querySelector('.offer-room-card.is-selected .js-offer-select-room');
  const roomTitle = selected?.dataset.roomTitle || '';
  const isApartment = (selected?.dataset.roomKind || '') === 'apartment';
  if (roomRow && roomLabel) {
    if (roomTitle) {
      roomLabel.textContent = `${isApartment ? ui('Apartament') : ui('Pokój')} ${roomTitle}`;
      roomRow.hidden = false;
    } else {
      roomLabel.textContent = '';
      roomRow.hidden = true;
    }
  }

  const lines = Array.isArray(quote?.lines) ? quote.lines : [];
  const roomQuote = quote?.room_quote || {};
  const priceLines = lines.filter((line) => {
    const key = String(line?.key || '');
    return key === 'stay' || key === 'accommodation' || key === 'skipass' || key === 'mandatory_insurance';
  });
  const canFallback = Boolean(selected) && !isPricingEnabled(root);

  if (quote || canFallback) {
    linesEl.replaceChildren();
  }

  if (priceLines.length) {
    priceLines.forEach((line) => {
      const key = String(line.key || '');
      if (key === 'mandatory_insurance') {
        const qty = Math.max(1, Number(line.quantity) || 1);
        const unit = formatMoneyAmount(line.unit_price, line.currency);
        const total = formatMoneyAmount(line.amount, line.currency);
        appendSumLine(
          linesEl,
          ui('Ubezpieczenie obowiązkowe'),
          qty > 1 ? `${unit} × ${qty} ${ui('os.')} = ${total}` : total,
          'TFG i TFP',
        );
        return;
      }
      const { title, hint } = splitPriceHint(line.label || '');
      appendSumLine(
        linesEl,
        ui(title) || ui('Cena pobytu'),
        formatMoneyAmount(line.amount, line.currency),
        translateStayHint(hint),
      );
    });
  } else if (canFallback && selected?.dataset.roomPrice) {
    const currency = (root.dataset.currency || sidebar?.querySelector('.js-offer-currency')?.textContent || 'PLN').trim();
    appendSumLine(linesEl, ui('Cena pobytu'), `${selected.dataset.roomPrice} ${currency}`.trim());
  }

  const roomIndex = selected?.dataset.roomIndex ?? '';
  if (sidebar) {
    if (!selected) {
      sidebar.dataset.sumRoomIndex = '';
    } else if (quote?.room_quote || canFallback) {
      sidebar.dataset.sumRoomIndex = roomIndex;
    } else if (sidebar.dataset.sumRoomIndex !== roomIndex) {
      sidebar.dataset.sumRoomIndex = '';
    }
  }

  if (totalEl) {
    if (quote?.total_formatted) {
      totalEl.textContent = quote.total_formatted;
    } else if (canFallback && selected?.dataset.roomPrice) {
      const currency = (root.dataset.currency || 'PLN').trim();
      totalEl.textContent = `${selected.dataset.roomPrice} ${currency}`.trim();
    } else if (quote) {
      totalEl.textContent = '—';
    } else if (selected?.dataset.roomPrice && sidebar?.dataset.sumRoomIndex !== roomIndex) {
      const currency = (root.dataset.currency || 'PLN').trim();
      totalEl.textContent = Number.isFinite(Number(selected.dataset.roomPrice))
        ? formatMoneyAmount(selected.dataset.roomPrice, currency)
        : selected.dataset.roomPrice;
    }
  }

  if (noteEl) {
    noteEl.textContent = isApartment || roomQuote.is_apartment
      ? i18n(
        'sum_note_apartment',
        'Cena jest za cały apartament, niezależnie od liczby osób. Skipass możesz doliczyć powyżej. Ubezpieczenie obowiązkowe (TFG i TFP) doliczane jest osobno.',
      )
      : i18n(
        'sum_note_skipass',
        'Cena pobytu zawiera skipass. Ubezpieczenie obowiązkowe (TFG i TFP) doliczane jest osobno. Chcesz ofertę bez skipassu? Napisz do nas.',
      );
  }
};

const formatTermOptionText = (option) => {
  const from = option.dataset.dateFrom || '';
  const to = option.dataset.dateTo || '';
  // Nights are shown in `.js-offer-nights` badge — keep select text dates-only.
  if (from && to) {
    return `${from} → ${to}`;
  }
  return option.textContent.trim();
};

const ensureSelectHasValue = (select, value) => {
  if (!select) return;
  const next = String(value);
  if (![...select.options].some((option) => option.value === next)) {
    const option = document.createElement('option');
    option.value = next;
    option.textContent = next;
    select.appendChild(option);
  }
  select.value = next;
};

const getOfferDrawer = (root) => {
  const id = root.dataset.offerSidebarMobileId || '';
  if (!id) return null;
  return document.body.querySelector(`.offer-page__sidebar-drawer[data-offer-root-id="${id}"]`);
};

/**
 * Live sidebar only — never the relocate placeholder clone left under the
 * gallery while the real aside sits in the mobile drawer. Hitting the clone
 * made Potwierdź update hidden selects/label while the visible trigger stayed
 * at „0 dzieci” (quote still looked fine if ages were written elsewhere).
 */
const getOfferSidebar = (root) =>
  root.querySelector('.js-offer-sidebar:not(.js-sticky-summary-inline-placeholder)')
  || getOfferDrawer(root)?.querySelector('.js-offer-sidebar:not(.js-sticky-summary-inline-placeholder)')
  || getOfferDrawer(root)?.querySelector('.js-offer-sidebar');

const getTermSelect = (root) => getOfferSidebar(root)?.querySelector('.js-offer-term') || null;

const getTermSelectWrap = (root) => getTermSelect(root)?.closest('.js-offer-term-select') || null;

const closeTermSelect = (wrap) => {
  if (!wrap) return;
  wrap.classList.remove('is-open');
  wrap.querySelector('.js-offer-term-trigger')?.setAttribute('aria-expanded', 'false');
  const panel = wrap.querySelector('.js-offer-term-panel');
  if (panel) panel.hidden = true;
};

const syncTermSelectUi = (wrap) => {
  if (!wrap) return;
  const select = wrap.querySelector('.js-offer-term');
  const valueEl = wrap.querySelector('.js-offer-term-value');
  if (!select || !valueEl) return;
  const option = select.selectedOptions[0];
  valueEl.textContent = option?.textContent.trim() || '';
  wrap.querySelectorAll('.js-offer-term-option').forEach((btn) => {
    btn.classList.toggle('is-selected', btn.dataset.value === select.value);
  });
};

const getGuestCounts = (root) => {
  const sidebar = getOfferSidebar(root);
  const adultsSelect = sidebar?.querySelector('.js-offer-adults') || root.querySelector('.js-offer-adults');
  const childrenSelect = sidebar?.querySelector('.js-offer-children') || root.querySelector('.js-offer-children');
  return {
    adults: Number(adultsSelect?.value || 2),
    children: Number(childrenSelect?.value || 0),
  };
};

const isPricingEnabled = (root) => (
  root?.dataset?.pricingEnabled === '1'
  || getOfferSidebar(root)?.dataset?.pricingEnabled === '1'
);

const isHidePriceOld = (root) => (
  root?.dataset?.hidePriceOld === '1'
  || getOfferSidebar(root)?.dataset?.hidePriceOld === '1'
);

const ageFromDob = (dob, asOfIso = '') => {
  const raw = String(dob || '').trim();
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const now = asOfIso ? new Date(`${asOfIso}T12:00:00`) : new Date();
  if (Number.isNaN(now.getTime())) return null;
  let age = now.getFullYear() - d.getFullYear();
  const md = (now.getMonth() + 1) * 100 + now.getDate();
  const bmd = (d.getMonth() + 1) * 100 + d.getDate();
  if (md < bmd) age -= 1;
  return Math.max(0, age);
};

const MIN_CHILD_AGE = 0;
const MAX_CHILD_AGE = 17;
const DEFAULT_CHILD_AGE = 8;
const AGE_CHEVRON = '<svg class="offer-page__guests-age-chevron" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m5 7.5 5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
let offerChildAgeSeq = 0;

const formatChildAgeLabel = (n) => {
  if (n === 1) return '1 rok';
  if (n >= 2 && n <= 4) return `${n} lata`;
  return `${n} lat`;
};

const clampChildAge = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_CHILD_AGE;
  return Math.min(MAX_CHILD_AGE, Math.max(MIN_CHILD_AGE, Math.round(n)));
};

const getChildAgesStore = (root) => (
  getOfferSidebar(root)?.querySelector('.js-offer-child-ages-store')
  || root.querySelector('.js-offer-child-ages-store')
);

const getChildAges = (root) => {
  const store = getChildAgesStore(root);
  const stored = [...(store?.querySelectorAll('input') || [])]
    .map((el) => clampChildAge(el.value));
  if (stored.length) return stored;
  const { children } = getGuestCounts(root);
  return children > 0 ? Array.from({ length: children }, () => DEFAULT_CHILD_AGE) : [];
};

const setChildAges = (root, ages) => {
  const store = getChildAgesStore(root);
  if (!store) return;
  const next = (Array.isArray(ages) ? ages : []).map((n) => clampChildAge(n));
  store.replaceChildren();
  next.forEach((age) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.value = String(age);
    store.appendChild(input);
  });
};

const closeAgeSelect = (select) => {
  select.classList.remove('is-open');
  const trigger = select.querySelector('.js-offer-age-trigger');
  const list = select.querySelector('.js-offer-age-list');
  trigger?.setAttribute('aria-expanded', 'false');
  if (list) list.hidden = true;
};

const closeAllAgeSelects = (picker) => {
  picker?.querySelectorAll('.js-offer-age-select.is-open').forEach(closeAgeSelect);
};

const setAgeSelectValue = (select, age) => {
  const next = clampChildAge(age);
  const input = select.querySelector('.js-offer-child-age');
  const valueEl = select.querySelector('.js-offer-age-value');
  if (input) input.value = String(next);
  if (valueEl) valueEl.textContent = formatChildAgeLabel(next);
  select.dataset.value = String(next);
  select.querySelectorAll('.js-offer-age-option').forEach((option) => {
    const selected = Number(option.dataset.value) === next;
    option.classList.toggle('is-selected', selected);
    option.setAttribute('aria-selected', String(selected));
  });
};

const bindAgeSelect = (select, picker) => {
  const trigger = select.querySelector('.js-offer-age-trigger');
  const list = select.querySelector('.js-offer-age-list');
  if (!trigger || !list) return;

  trigger.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const willOpen = !select.classList.contains('is-open');
    closeAllAgeSelects(picker);
    if (!willOpen) return;
    select.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    list.hidden = false;
    list.querySelector('.js-offer-age-option.is-selected')?.focus();
  });

  list.querySelectorAll('.js-offer-age-option').forEach((option) => {
    option.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setAgeSelectValue(select, option.dataset.value);
      closeAgeSelect(select);
    });
  });
};

const renderChildAgeFields = (picker, ages) => {
  const wrap = picker.querySelector('.js-offer-child-ages');
  if (!wrap) return;
  const count = ages.length;
  wrap.hidden = count <= 0;
  wrap.replaceChildren();
  ages.forEach((rawAge, index) => {
    const age = clampChildAge(rawAge);
    const labelText = count === 1 ? 'Wiek dziecka' : `Wiek dziecka ${index + 1}`;
    const fieldId = `offer-child-age-${++offerChildAgeSeq}`;
    const options = [];
    for (let n = MIN_CHILD_AGE; n <= MAX_CHILD_AGE; n += 1) {
      const selected = n === age;
      options.push(
        `<li class="offer-page__guests-age-option js-offer-age-option${selected ? ' is-selected' : ''}" role="option" tabindex="-1" data-value="${n}" aria-selected="${selected}">${formatChildAgeLabel(n)}</li>`,
      );
    }

    const row = document.createElement('div');
    row.className = 'offer-page__guests-age';
    row.innerHTML = `
      <span class="offer-page__guests-age-label" id="${fieldId}-label">${labelText}</span>
      <div class="offer-page__guests-age-select js-offer-age-select" data-value="${age}">
        <input type="hidden" class="js-offer-child-age" value="${age}">
        <button
          type="button"
          class="offer-page__guests-age-trigger js-offer-age-trigger"
          aria-expanded="false"
          aria-haspopup="listbox"
          aria-controls="${fieldId}-list"
          aria-labelledby="${fieldId}-label"
        >
          <span class="offer-page__guests-age-value js-offer-age-value">${formatChildAgeLabel(age)}</span>
          ${AGE_CHEVRON}
        </button>
        <ul class="offer-page__guests-age-list js-offer-age-list" id="${fieldId}-list" role="listbox" aria-labelledby="${fieldId}-label" hidden>
          ${options.join('')}
        </ul>
      </div>
    `;
    wrap.appendChild(row);
    bindAgeSelect(row.querySelector('.js-offer-age-select'), picker);
  });
};

const draftChildAges = (picker) => (
  [...picker.querySelectorAll('.js-offer-child-age')].map((el) => clampChildAge(el.value))
);

const apartmentSkipassOptIn = (root) => Boolean(
  getOfferSidebar(root)?.querySelector('.js-offer-skip-apartment')?.checked
);

/** API skip_apartment = bez skipassu. Hotel: zawsze wliczony. Apartament: HTML opt-in („dolicz skipass”). */
const isSkipApartment = (root) => {
  const wrap = getOfferSidebar(root)?.querySelector('.js-offer-skip-apartment-wrap');
  if (!wrap || wrap.hidden) return false;
  return !apartmentSkipassOptIn(root);
};

const formatStayAmount = (amount, currency) => {
  const n = Math.round(Number(amount) || 0);
  const formatted = n.toLocaleString('pl-PL');
  const suffix = String(currency || 'PLN').toUpperCase() === 'PLN' ? 'zł' : currency;
  return `${formatted} ${suffix}`;
};

/** Sidebar „od”: brak pokoju mieszczącego obsadę / z ceną > 0. */
const NO_ROOM_FOR_PARTY_LABEL = () => i18n('no_room', 'Brak pokoju dla tylu osób');
/** Pokój niedostępny w wybranym turnusie (pricing_unavailable / brak override). */
const ROOM_SELECT_LABEL = () => ui('Wybierz');

const roomListingAmount = (row) => Number(row?.listing_amount ?? row?.stay_total) || 0;

const roomIsAvailable = (row) => row?.available !== false;

const roomHasPositivePrice = (row) => roomListingAmount(row) > 0;

/** Pokój kwalifikuje się do „cena od” i wyboru. */
const roomIsPricedFit = (row) => Boolean(row?.fits) && roomIsAvailable(row) && roomHasPositivePrice(row);

const resolveUnavailableLabel = (card, summary) => {
  const fromSummary = String(summary?.unavailable_label ?? '').trim();
  const fromData = String(card?.dataset?.unavailableLabel ?? '').trim();
  return ui(fromSummary || fromData || 'niedostępny w tym terminie');
};

const paintUnavailableLabel = (card, label) => {
  if (!card) return;
  card.style.setProperty('--room-unavailable-label', `"${String(label || '').replace(/"/g, '')}"`);
};

const setSelectControlText = (control, label, { showCheckbox }) => {
  if (!control) return;
  if (!control.matches('button, .js-offer-select-room')) {
    control.textContent = label;
    return;
  }
  let checkbox = control.querySelector('.offer-room-card__checkbox');
  if (showCheckbox) {
    if (!checkbox) {
      checkbox = document.createElement('span');
      checkbox.className = 'offer-room-card__checkbox';
      checkbox.setAttribute('aria-hidden', 'true');
      control.prepend(checkbox);
    }
    checkbox.hidden = false;
  } else if (checkbox) {
    checkbox.hidden = true;
  }
  [...control.childNodes].forEach((node) => {
    if (node !== checkbox) {
      node.remove();
    }
  });
  control.appendChild(document.createTextNode(showCheckbox ? ` ${label}` : label));
};

/**
 * SSR renders unavailable rooms as a <span> (no data-room-index). After term change
 * quote may make them available — upgrade the span to a real select button.
 */
const ensureRoomSelectButton = (card, index, summary) => {
  const existing = card.querySelector('.js-offer-select-room');
  if (existing) return existing;

  const placeholder = card.querySelector('.offer-room-card__select');
  const title = String(
    summary?.title
    || card.querySelector('.offer-room-card__title')?.textContent
    || '',
  ).replace(/\s+/g, ' ').trim();
  const kind = String(card.dataset.roomKind || summary?.kind || 'room');

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'offer-room-card__select js-offer-select-room';
  button.dataset.roomIndex = String(index);
  button.dataset.roomTitle = title;
  button.dataset.roomKind = kind;
  button.setAttribute('aria-pressed', 'false');

  if (placeholder) {
    placeholder.replaceWith(button);
  } else {
    card.querySelector('.offer-room-card__header-actions')?.appendChild(button);
  }
  return button;
};

const applyRoomSummaries = (root, rooms) => {
  if (!Array.isArray(rooms)) return;
  const byIndex = new Map();
  rooms.forEach((row) => {
    if (row && Number.isFinite(Number(row.index))) {
      byIndex.set(Number(row.index), row);
    }
  });
  root.querySelectorAll('.offer-room-card').forEach((card, index) => {
    const summary = byIndex.get(index) || rooms[index];
    let button = card.querySelector('.js-offer-select-room');
    const disabledSelect = card.querySelector('.offer-room-card__select--disabled');
    const priceEl = card.querySelector('.js-offer-room-price') || card.querySelector('.offer-room-card__price');
    const oldEl = card.querySelector('.js-offer-room-price-old');
    const available = roomIsAvailable(summary);
    const selectable = roomIsPricedFit(summary);
    const unavailableLabel = resolveUnavailableLabel(card, summary);
    if (!available) paintUnavailableLabel(card, unavailableLabel);
    card.classList.toggle('offer-room-card--unavailable', !available);
    card.classList.toggle('offer-room-card--nofit', !selectable);

    // Usuń ewentualny status przy tytule (stary SSR / cache).
    card.querySelector('.offer-room-card__status')?.remove();

    if (!available) {
      const selectControl = button || disabledSelect;
      if (button) {
        button.disabled = true;
        button.classList.add('offer-room-card__select--disabled');
      }
      if (selectControl) {
        setSelectControlText(selectControl, unavailableLabel, { showCheckbox: false });
      }
    } else {
      // Term change: SSR span → button so „Wybierz” works for newly available rooms.
      button = ensureRoomSelectButton(card, index, summary);
      button.disabled = !selectable;
      button.classList.toggle('offer-room-card__select--disabled', !selectable);
      setSelectControlText(button, ROOM_SELECT_LABEL(), { showCheckbox: true });
      if (selectable && summary?.stay_formatted) {
        button.dataset.roomPrice = String(summary.stay_total ?? '');
      } else {
        button.dataset.roomPrice = '';
      }
    }
    if (priceEl) {
      if (selectable && summary?.stay_formatted) {
        priceEl.textContent = summary.stay_formatted;
      } else {
        priceEl.textContent = '';
      }
    }
    if (oldEl) {
      if (selectable && !isHidePriceOld(root) && summary?.price_old_formatted) {
        oldEl.hidden = false;
        oldEl.textContent = summary.price_old_formatted;
      } else {
        oldEl.hidden = true;
        oldEl.textContent = '';
      }
    }
    if (!selectable && card.classList.contains('is-selected')) {
      card.classList.remove('is-selected');
      button?.setAttribute('aria-pressed', 'false');
    }
  });
};

const clearSelectedRooms = (root) => {
  root.querySelectorAll('.offer-room-card.is-selected').forEach((card) => {
    card.classList.remove('is-selected');
    card.querySelector('.js-offer-select-room')?.setAttribute('aria-pressed', 'false');
  });
};

const syncPickedSidebarUi = (root) => {
  const selected = root.querySelector('.offer-room-card.is-selected .js-offer-select-room');
  const drawer = getOfferDrawer(root);
  const sidebar = getOfferSidebar(root);
  const scrollCta = root.querySelector('.js-offer-scroll-rooms')
    || drawer?.querySelector('.js-offer-scroll-rooms');
  const bookingCta = root.querySelector('.offer-page__booking > .js-offer-booking-cta')
    || drawer?.querySelector('.offer-page__booking > .js-offer-booking-cta');
  const changeRoom = sidebar?.querySelector('.js-offer-change-room')
    || root.querySelector('.js-offer-change-room');
  const sumcard = sidebar?.querySelector('.js-offer-sumcard');
  const priceBlock = sidebar?.querySelector('.js-offer-price-block');
  const hasRoom = Boolean(selected);

  sidebar?.classList.toggle('is-picked', hasRoom);
  if (sumcard) sumcard.hidden = !hasRoom;
  if (priceBlock) priceBlock.hidden = hasRoom;
  if (scrollCta) scrollCta.hidden = hasRoom;
  if (bookingCta) bookingCta.hidden = !hasRoom;
  if (changeRoom) changeRoom.hidden = !hasRoom;

  return selected;
};

let quoteTimer = 0;
let quoteSeq = 0;
const fetchOfferQuote = async (root) => {
  if (!isPricingEnabled(root)) return null;
  const url = root.dataset.quoteUrl || '/wp-json/hski/v1/booking/quote';
  const { adults, children } = getGuestCounts(root);
  const selected = root.querySelector('.offer-room-card.is-selected .js-offer-select-room');
  const payload = {
    offer_id: Number(root.dataset.offerId || 0),
    details: {
      adults,
      children,
      children_ages: getChildAges(root),
      term: getTermSelect(root)?.value || '0',
      date_from: getTermSelect(root)?.selectedOptions?.[0]?.dataset.isoFrom || '',
      room_index: selected?.dataset.roomIndex ?? '',
      skip_apartment: isSkipApartment(root),
    },
  };
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) return null;
  return response.json().catch(() => null);
};

const getOfferPriceEls = (root) => {
  const sidebar = getOfferSidebar(root);
  return {
    prefix: sidebar?.querySelector('.js-offer-price-prefix') || root.querySelector('.js-offer-price-prefix'),
    price: sidebar?.querySelector('.js-offer-price') || root.querySelector('.js-offer-price'),
    currency: sidebar?.querySelector('.js-offer-currency') || root.querySelector('.js-offer-currency'),
    old: sidebar?.querySelector('.js-offer-price-old') || root.querySelector('.js-offer-price-old'),
  };
};

const rememberFromPrice = (root) => {
  const els = getOfferPriceEls(root);
  if (!els.price) return;
  root.dataset.fromPrice = els.price.textContent.trim();
  root.dataset.fromCurrency = (els.currency?.textContent || '').trim();
  root.dataset.fromPriceOld = (els.old && !els.old.hidden) ? els.old.textContent.trim() : '';
};

const restoreFromPrice = (root) => {
  const els = getOfferPriceEls(root);
  const fromPrice = (root.dataset.fromPrice || '').trim();
  const isNoRoom = fromPrice === NO_ROOM_FOR_PARTY_LABEL();
  if (els.prefix) els.prefix.hidden = isNoRoom;
  if (els.price && fromPrice) {
    els.price.textContent = fromPrice;
  }
  if (els.currency) {
    els.currency.textContent = isNoRoom ? '' : (root.dataset.fromCurrency || '');
  }
  if (els.old) {
    const oldText = isNoRoom ? '' : (root.dataset.fromPriceOld || '');
    els.old.textContent = oldText;
    els.old.hidden = !oldText;
  }
};

const applyOfferQuote = (root, quote) => {
  if (!quote) return;
  applyRoomSummaries(root, quote.rooms);
  syncPickedSidebarUi(root);
  const selected = root.querySelector('.offer-room-card.is-selected .js-offer-select-room');
  const prefix = getOfferSidebar(root)?.querySelector('.js-offer-price-prefix') || root.querySelector('.js-offer-price-prefix');
  const priceEl = getOfferSidebar(root)?.querySelector('.js-offer-price') || root.querySelector('.js-offer-price');
  const currencyEl = getOfferSidebar(root)?.querySelector('.js-offer-currency') || root.querySelector('.js-offer-currency');
  const oldEl = getOfferSidebar(root)?.querySelector('.js-offer-price-old') || root.querySelector('.js-offer-price-old');
  const skipWrap = getOfferSidebar(root)?.querySelector('.js-offer-skip-apartment-wrap');
  const kind = selected?.dataset.roomKind || selected?.closest('.offer-room-card')?.dataset.roomKind || '';
  if (skipWrap) skipWrap.hidden = kind !== 'apartment';

  const setOldPrice = (formatted) => {
    if (!oldEl) return;
    if (!isHidePriceOld(root) && formatted) {
      oldEl.hidden = false;
      oldEl.textContent = formatted;
    } else {
      oldEl.hidden = true;
      oldEl.textContent = '';
    }
  };

  if (priceEl) {
    if (selected && quote.room_quote) {
      // HTML: po wyborze pokoju „Razem” = pobyt + TFG/TFP (osobne waluty).
      if (quote.total_formatted) {
        priceEl.textContent = quote.total_formatted;
        if (currencyEl) currencyEl.textContent = '';
      } else {
        const stay = quote.room_quote.stay_total;
        const currency = quote.room_quote.currency || quote.currency;
        priceEl.textContent = String(Math.round(Number(stay) || 0));
        if (currencyEl) currencyEl.textContent = String(currency || '').toUpperCase() === 'PLN' ? 'zł' : currency;
      }
      if (prefix) prefix.hidden = true;
      setOldPrice('');
    } else if (!selected) {
      const cheapest = (quote.rooms || [])
        .filter((row) => roomIsPricedFit(row))
        .sort((a, b) => roomListingAmount(a) - roomListingAmount(b))[0];
      if (cheapest) {
        if (cheapest.stay_formatted && cheapest.skipass_currency && cheapest.skipass_currency !== cheapest.currency) {
          priceEl.textContent = cheapest.stay_formatted;
          if (currencyEl) currencyEl.textContent = '';
        } else {
          priceEl.textContent = String(Math.round(roomListingAmount(cheapest)));
          if (currencyEl) {
            currencyEl.textContent = String(cheapest.currency || '').toUpperCase() === 'PLN' ? 'zł' : cheapest.currency;
          }
        }
        // Pokój bez cenaPrzed → zostaw SSR / zapamiętaną „Cena przed rabatem” z oferty.
        setOldPrice(cheapest.price_old_formatted || root.dataset.fromPriceOld || '');
        const termOption = getTermSelect(root)?.selectedOptions?.[0];
        if (termOption) {
          termOption.dataset.price = String(Math.round(roomListingAmount(cheapest)));
        }
        if (prefix) prefix.hidden = false;
        rememberFromPrice(root);
      } else {
        priceEl.textContent = NO_ROOM_FOR_PARTY_LABEL();
        if (currencyEl) currencyEl.textContent = '';
        if (prefix) prefix.hidden = true;
        setOldPrice('');
        rememberFromPrice(root);
      }
    }
  }
  if (!(selected && !quote.room_quote)) {
    applyOfferSummary(root, quote);
  }
};

const refreshOfferQuote = (root) => {
  if (!isPricingEnabled(root)) return;
  window.clearTimeout(quoteTimer);
  const seq = ++quoteSeq;
  quoteTimer = window.setTimeout(async () => {
    try {
      const quote = await fetchOfferQuote(root);
      if (seq !== quoteSeq) return;
      applyOfferQuote(root, quote);
      updateBookingLinks(root);
      mobileSidebarSyncers.get(root)?.sync?.();
    } catch {
      /* keep last displayed price */
    }
  }, 80);
};

const updateGuestsTriggerLabel = (root) => {
  const sidebar = getOfferSidebar(root);
  const labelEl = sidebar?.querySelector('.js-offer-guests-label') || root.querySelector('.js-offer-guests-label');
  if (!labelEl) return;
  const { adults, children } = getGuestCounts(root);
  labelEl.textContent = guestsLabel(adults, children);
};

const setGuestCounts = (root, adults, children) => {
  const sidebar = getOfferSidebar(root);
  const adultsSelect = sidebar?.querySelector('.js-offer-adults') || root.querySelector('.js-offer-adults');
  const childrenSelect = sidebar?.querySelector('.js-offer-children') || root.querySelector('.js-offer-children');
  ensureSelectHasValue(adultsSelect, Math.max(1, Number(adults) || 1));
  ensureSelectHasValue(childrenSelect, Math.max(0, Number(children) || 0));
  updateGuestsTriggerLabel(root);
};

const updateBookingLinks = (root) => {
  const offerId = root.dataset.offerId || '';
  const baseUrl = (root.dataset.bookingUrl || './rezerwacja.html').trim().split('?')[0] || './rezerwacja.html';
  const termSelect = getTermSelect(root);
  const { adults, children } = getGuestCounts(root);
  const termIndex = termSelect?.value ?? '0';
  const selectedRoom = root.querySelector('.offer-room-card.is-selected .js-offer-select-room');

  let url = `${baseUrl}?offer=${offerId}&adults=${adults}&children=${children}&term=${termIndex}`;
  getChildAges(root).forEach((age) => {
    url += `&children_ages[]=${encodeURIComponent(age)}`;
  });
  if (isSkipApartment(root)) {
    url += '&skip_apartment=1';
  }
  if (selectedRoom) {
    const roomIndex = selectedRoom.dataset.roomIndex ?? '';
    const roomTitle = selectedRoom.dataset.roomTitle || '';
    if (roomIndex !== '') {
      url += `&room=${encodeURIComponent(roomIndex)}`;
    }
    if (roomTitle) {
      url += `&room_title=${encodeURIComponent(roomTitle)}`;
    }
  }

  const applyBookingHref = (link) => {
    if (link.classList.contains('js-offer-scroll-rooms')) return;
    if (link.classList.contains('js-offer-select-room')) return;
    link.setAttribute('href', url);
  };
  root.querySelectorAll('.js-offer-booking-cta').forEach(applyBookingHref);
  getOfferSidebar(root)?.querySelectorAll('.js-offer-booking-cta').forEach(applyBookingHref);
};

const updatePrice = (root) => {
  if (isPricingEnabled(root)) {
    refreshOfferQuote(root);
    return;
  }
  const sidebar = getOfferSidebar(root);
  const termSelect = getTermSelect(root);
  const priceEl = sidebar?.querySelector('.js-offer-price') || root.querySelector('.js-offer-price');
  if (!termSelect || !priceEl) return;

  const option = termSelect.selectedOptions[0];
  const price = option?.dataset.price;
  if (price) {
    priceEl.textContent = price;
  }
};

const updateTermNightsBadge = (root) => {
  const termSelect = getTermSelect(root);
  if (!termSelect) return;
  const nightsEl = termSelect.closest('.js-offer-term-select')?.querySelector('.js-offer-nights')
    || getOfferSidebar(root)?.querySelector('.js-offer-nights')
    || root.querySelector('.js-offer-nights');
  const nights = resolveTermNights(root);
  if (nightsEl) nightsEl.textContent = nights ? nightsLabel(nights) : '';
  const daysText = skipassDaysLabel(nights);
  getOfferSidebar(root)?.querySelectorAll('.js-offer-skipass-label').forEach((el) => {
    let base = String(el.dataset.labelBase || '').trim();
    if (!base) {
      base = String(el.textContent || '')
        .replace(/\s*\(\d+\s*(?:dzień|dni)\)\s*$/i, '')
        .trim() || 'Skipass w cenie';
      el.dataset.labelBase = base;
    }
    el.textContent = daysText ? `${base} (${daysText})` : base;
  });
};

const syncGuestSelects = (root) => {
  const termSelect = getTermSelect(root);
  if (!termSelect) return;

  const option = termSelect.selectedOptions[0];
  if (!option?.dataset.adults) return;
  setGuestCounts(
    root,
    option.dataset.adults,
    option.dataset.children !== undefined ? option.dataset.children : getGuestCounts(root).children,
  );
};

const closeGuestsPicker = (picker) => {
  if (!picker) return;
  closeAllAgeSelects(picker);
  const trigger = picker.querySelector('.js-offer-guests-trigger');
  const panel = picker.querySelector('.js-offer-guests-panel');
  picker.classList.remove('is-open');
  trigger?.setAttribute('aria-expanded', 'false');
  if (panel) {
    panel.hidden = true;
    panel.style.removeProperty('--offer-guests-panel-max-height');
  }
  if (picker._guestsPanelResize) {
    window.removeEventListener('resize', picker._guestsPanelResize);
    picker._guestsPanelResize = null;
  }
};

/** Max-height = remaining viewport below panel top (sticky sidebar + 4 kids). */
const syncGuestsPanelMaxHeight = (picker) => {
  const panel = picker?.querySelector('.js-offer-guests-panel');
  if (!panel || panel.hidden || !picker.classList.contains('is-open')) return;
  const top = panel.getBoundingClientRect().top;
  const gutter = 16;
  const available = Math.floor(window.innerHeight - top - gutter);
  // Keep room for Potwierdź + padding even when ages list is empty.
  const minUsable = 220;
  const maxHeight = Math.max(minUsable, available);
  panel.style.setProperty('--offer-guests-panel-max-height', `${maxHeight}px`);
};

const PLUS_ICON = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 4.5V19.5M19.5 12H4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
const MINUS_ICON = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M19.5 12H4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

/**
 * Stary combo <select class="js-offer-guests"> → picker jak na prodzie.
 * Działa nawet gdy serwer nadal serwuje legacy markup.
 */
const upgradeLegacyGuestsSelect = (root) => {
  if (root.querySelector('.js-offer-guests-picker')) return;

  const legacySelect = root.querySelector('select.js-offer-guests');
  if (!legacySelect) return;

  const selected = legacySelect.selectedOptions[0];
  const adults = Math.max(1, Number(selected?.dataset.adults || 2));
  const children = Math.max(0, Number(selected?.dataset.children || 0));

  const wrap = document.createElement('div');
  wrap.className = 'offer-page__guests js-offer-guests-picker';
  wrap.dataset.maxAdults = '6';
  wrap.dataset.maxChildren = '4';
  wrap.innerHTML = `
    <button type="button" class="offer-page__guests-trigger js-offer-guests-trigger" aria-expanded="false" aria-haspopup="dialog">
      <span class="js-offer-guests-label">${guestsLabel(adults, children)}</span>
    </button>
    <div class="offer-page__guests-panel js-offer-guests-panel" hidden>
      <div class="offer-page__guests-panel-body">
        <div class="offer-page__guests-row">
          <span class="offer-page__guests-row-label">${i18n('adults_label', 'Dorośli')}</span>
          <div class="offer-page__guests-amount">
            <button type="button" class="offer-page__guests-btn js-offer-adults-minus" aria-label="${i18n('less_adults', 'Mniej dorosłych')}">${MINUS_ICON}</button>
            <span class="offer-page__guests-value js-offer-adults-value" aria-live="polite">${adults}</span>
            <button type="button" class="offer-page__guests-btn js-offer-adults-plus" aria-label="${i18n('more_adults', 'Więcej dorosłych')}">${PLUS_ICON}</button>
          </div>
        </div>
        <div class="offer-page__guests-row">
          <span class="offer-page__guests-row-label">${i18n('children_label', 'Dzieci')}</span>
          <div class="offer-page__guests-amount">
            <button type="button" class="offer-page__guests-btn js-offer-children-minus" aria-label="${i18n('less_children', 'Mniej dzieci')}"${children <= 0 ? ' hidden' : ''}>${MINUS_ICON}</button>
            <span class="offer-page__guests-value js-offer-children-value" aria-live="polite">${children}</span>
            <button type="button" class="offer-page__guests-btn js-offer-children-plus" aria-label="${i18n('more_children', 'Więcej dzieci')}">${PLUS_ICON}</button>
          </div>
        </div>
        <div class="offer-page__guests-ages js-offer-child-ages" hidden></div>
      </div>
      <button type="button" class="btn btn--primary btn--block btn--no-arrow offer-page__guests-confirm js-offer-guests-confirm">${i18n('confirm', 'Potwierdź')}</button>
    </div>
  `;

  const field = legacySelect.closest('label.offer-page__field') || legacySelect;
  field.replaceWith(wrap);

  // Ukryte selecty adults/children — wymagane przez setGuestCounts / booking URL.
  let hiddenRow = root.querySelector('.offer-page__selects-row');
  if (!hiddenRow) {
    const selects = root.querySelector('.offer-page__selects') || wrap.parentElement;
    hiddenRow = document.createElement('div');
    hiddenRow.className = 'offer-page__selects-row visually-hidden';
    hiddenRow.setAttribute('aria-hidden', 'true');
    hiddenRow.innerHTML = `
      <select class="js-offer-adults" tabindex="-1"></select>
      <select class="js-offer-children" tabindex="-1"></select>
      <div class="js-offer-child-ages-store"></div>
    `;
    selects?.appendChild(hiddenRow);
  }
  if (!hiddenRow.querySelector('.js-offer-child-ages-store')) {
    const store = document.createElement('div');
    store.className = 'js-offer-child-ages-store';
    hiddenRow.appendChild(store);
  }

  setGuestCounts(root, adults, children);
};

const renderGuestsDraft = (picker, draft) => {
  const maxAdults = Number(picker.dataset.maxAdults || 6);
  const maxChildren = Number(picker.dataset.maxChildren || 4);
  const adults = Math.min(maxAdults, Math.max(1, Number(draft.adults) || 1));
  const children = Math.min(maxChildren, Math.max(0, Number(draft.children) || 0));
  draft.adults = adults;
  draft.children = children;

  const adultsValue = picker.querySelector('.js-offer-adults-value');
  const childrenValue = picker.querySelector('.js-offer-children-value');
  if (adultsValue) adultsValue.textContent = String(adults);
  if (childrenValue) childrenValue.textContent = String(children);

  const adultsMinus = picker.querySelector('.js-offer-adults-minus');
  const adultsPlus = picker.querySelector('.js-offer-adults-plus');
  const childrenMinus = picker.querySelector('.js-offer-children-minus');
  const childrenPlus = picker.querySelector('.js-offer-children-plus');
  if (adultsMinus) adultsMinus.disabled = adults <= 1;
  if (adultsPlus) adultsPlus.disabled = adults >= maxAdults;
  if (childrenMinus) {
    childrenMinus.hidden = children <= 0;
    childrenMinus.disabled = children <= 0;
  }
  if (childrenPlus) childrenPlus.disabled = children >= maxChildren;
  const ages = Array.isArray(draft.ages) ? draft.ages.slice(0, children) : [];
  while (ages.length < children) ages.push(DEFAULT_CHILD_AGE);
  draft.ages = ages;
  renderChildAgeFields(picker, ages);
  if (picker.classList.contains('is-open')) {
    // Age rows change panel height — re-measure remaining viewport.
    requestAnimationFrame(() => syncGuestsPanelMaxHeight(picker));
  }
};

const openGuestsPicker = (root, picker) => {
  closeTermSelect(getTermSelectWrap(root));
  root.querySelectorAll('.js-offer-guests-picker.is-open').forEach((openPicker) => {
    if (openPicker !== picker) closeGuestsPicker(openPicker);
  });

  const { adults, children } = getGuestCounts(root);
  const ages = getChildAges(root);
  while (ages.length < children) ages.push(DEFAULT_CHILD_AGE);
  const draft = { adults, children, ages: ages.slice(0, children) };
  picker._draft = draft;
  renderGuestsDraft(picker, draft);

  const trigger = picker.querySelector('.js-offer-guests-trigger');
  const panel = picker.querySelector('.js-offer-guests-panel');
  picker.classList.add('is-open');
  trigger?.setAttribute('aria-expanded', 'true');
  if (panel) panel.hidden = false;
  requestAnimationFrame(() => syncGuestsPanelMaxHeight(picker));
  if (!picker._guestsPanelResize) {
    picker._guestsPanelResize = () => syncGuestsPanelMaxHeight(picker);
    window.addEventListener('resize', picker._guestsPanelResize, { passive: true });
  }
};

const initOfferGuestsPicker = (root) => {
  const picker = getOfferSidebar(root)?.querySelector('.js-offer-guests-picker')
    || root.querySelector('.js-offer-guests-picker');
  if (!picker || picker.dataset.bound === '1') return;
  picker.dataset.bound = '1';

  // Stary markup bez panel-body: owiń wiersze/wieki, zostaw Potwierdź jako stopkę.
  const panel = picker.querySelector('.js-offer-guests-panel');
  if (panel && !panel.querySelector('.offer-page__guests-panel-body')) {
    const body = document.createElement('div');
    body.className = 'offer-page__guests-panel-body';
    const confirm = panel.querySelector('.js-offer-guests-confirm');
    [...panel.childNodes].forEach((node) => {
      if (node === confirm) return;
      body.appendChild(node);
    });
    if (confirm) panel.insertBefore(body, confirm);
    else panel.appendChild(body);
  }

  const trigger = picker.querySelector('.js-offer-guests-trigger');
  const confirmBtn = picker.querySelector('.js-offer-guests-confirm');

  trigger?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (picker.classList.contains('is-open')) {
      closeGuestsPicker(picker);
      return;
    }
    openGuestsPicker(root, picker);
  });

  picker.querySelector('.js-offer-adults-minus')?.addEventListener('click', (event) => {
    event.stopPropagation();
    const draft = picker._draft || { ...getGuestCounts(root) };
    draft.adults = Math.max(1, draft.adults - 1);
    picker._draft = draft;
    renderGuestsDraft(picker, draft);
  });
  picker.querySelector('.js-offer-adults-plus')?.addEventListener('click', (event) => {
    event.stopPropagation();
    const draft = picker._draft || { ...getGuestCounts(root) };
    draft.adults += 1;
    picker._draft = draft;
    renderGuestsDraft(picker, draft);
  });
  picker.querySelector('.js-offer-children-minus')?.addEventListener('click', (event) => {
    event.stopPropagation();
    const draft = picker._draft || { ...getGuestCounts(root) };
    draft.ages = draftChildAges(picker);
    draft.children = Math.max(0, draft.children - 1);
    draft.ages = (draft.ages || []).slice(0, draft.children);
    picker._draft = draft;
    renderGuestsDraft(picker, draft);
  });
  picker.querySelector('.js-offer-children-plus')?.addEventListener('click', (event) => {
    event.stopPropagation();
    const draft = picker._draft || { ...getGuestCounts(root) };
    draft.ages = draftChildAges(picker);
    draft.children += 1;
    draft.ages = [...(draft.ages || []), DEFAULT_CHILD_AGE];
    picker._draft = draft;
    renderGuestsDraft(picker, draft);
  });

  confirmBtn?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const previous = getGuestCounts(root);
    const draft = picker._draft || previous;
    draft.ages = draftChildAges(picker);
    const nextAdults = Math.max(1, Number(draft.adults) || 1);
    const nextChildren = Math.max(0, Number(draft.children) || 0);
    draft.adults = nextAdults;
    draft.children = nextChildren;
    const occupancyChanged = Number(previous.adults) !== nextAdults
      || Number(previous.children) !== nextChildren;

    setGuestCounts(root, nextAdults, nextChildren);
    setChildAges(root, (draft.ages || []).slice(0, nextChildren));
    // Picker reference is the live node (bound at init) — keep its trigger in sync
    // even if a stale placeholder still matches a broad sidebar query.
    const liveLabel = picker.querySelector('.js-offer-guests-label');
    if (liveLabel) liveLabel.textContent = guestsLabel(nextAdults, nextChildren);
    closeGuestsPicker(picker);

    if (occupancyChanged) {
      // Keep room selected until quote says !fits (capacity exceeded).
      syncRoomBookingCta(root, { quote: false });
      updatePrice(root);
      return;
    }

    updateBookingLinks(root);
    updatePrice(root);
  });

  picker.querySelector('.js-offer-guests-panel')?.addEventListener('click', (event) => {
    event.stopPropagation();
    if (!event.target.closest('.js-offer-age-select')) {
      closeAllAgeSelects(picker);
    }
  });

  document.addEventListener('click', (event) => {
    if (!picker.classList.contains('is-open')) return;
    if (picker.contains(event.target)) return;
    closeGuestsPicker(picker);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !picker.classList.contains('is-open')) return;
    const openAge = picker.querySelector('.js-offer-age-select.is-open');
    if (openAge) {
      event.preventDefault();
      closeAgeSelect(openAge);
      return;
    }
    closeGuestsPicker(picker);
  });
};

const mobileSidebarSyncers = new WeakMap();

const initOfferSidebarMobile = (root) => {
  const sidebar = root.querySelector('.js-offer-sidebar');
  if (!sidebar) return;

  const ROOMS_SECTION_ID = 'available-rooms';

  // Belka eSky + sidebar karta pod galerią (native shell trzyma aside
  // w osobnej kolumnie → na mobile byłby dopiero po całej treści).
  // Sheet: relocate (prawdziwy aside) + placeholder w slocie, żeby karta nie znikała.
  const api = initStickySummaryBar({
    root,
    sidebar,
    variant: 'course-pick',
    keepSidebarInline: true,
    drawerSidebarMode: 'relocate',
    inlineMountAfter: '.offer-page__gallery, .js-offer-gallery',
    autoOpenAtBottom: false,
    drawerLabel: ui('Szczegóły rezerwacji'),
    getTotalText: () => {
      const stripOd = (text) => String(text || '').replace(/\s+/g, ' ').trim().replace(/^od\s+/i, '').trim();
      const sidebarEl = getOfferSidebar(root);
      const picked = root.querySelector('.offer-room-card.is-selected .js-offer-select-room');
      if (sidebarEl?.classList.contains('is-picked') || picked) {
        const roomIndex = picked?.dataset.roomIndex ?? '';
        const sumMatchesRoom = Boolean(roomIndex)
          && (sidebarEl?.dataset.sumRoomIndex ?? '') === roomIndex;
        const sum = sidebarEl?.querySelector('.js-offer-sum-total');
        const sumText = stripOd(sum?.textContent);
        if (sumMatchesRoom && sumText && sumText !== '—') return sumText;
        const roomPrice = picked?.dataset.roomPrice?.trim();
        if (roomPrice) {
          const currency = (root.dataset.currency || 'PLN').trim();
          return Number.isFinite(Number(roomPrice))
            ? formatMoneyAmount(roomPrice, currency)
            : stripOd(roomPrice);
        }
        return '';
      }
      const current = sidebarEl?.querySelector('.js-offer-price')
        || sidebarEl?.querySelector('.offer-page__price-current .js-offer-price');
      if (current) {
        const amount = stripOd(current.textContent);
        const currency = (sidebarEl?.querySelector('.js-offer-currency')?.textContent
          || root.dataset.currency
          || 'PLN').replace(/\s+/g, ' ').trim();
        if (amount && !/(zł|pln|€|\$)/i.test(amount)) {
          return formatMoneyAmount(amount.replace(/\s/g, '').replace(',', '.'), currency);
        }
        return amount;
      }
      const priceEl = root.querySelector('.js-offer-price');
      const currency = (root.dataset.currency || 'PLN').trim();
      return priceEl ? formatMoneyAmount(priceEl.textContent.trim(), currency) : '';
    },
    getPriceCaption: () => {
      const sidebarEl = getOfferSidebar(root);
      const picked = root.querySelector('.offer-room-card.is-selected');
      if (sidebarEl?.classList.contains('is-picked') || picked) return ui('Razem');
      return ui('Od');
    },
    getCta: () => {
      const drawer = getOfferDrawer(root);
      const sidebarEl = getOfferSidebar(root);
      const roomPicked = Boolean(
        root.querySelector('.offer-room-card.is-selected')
        || sidebarEl?.classList.contains('is-picked'),
      );
      const bookingCta = sidebarEl?.querySelector('.js-offer-booking-cta')
        || drawer?.querySelector('.js-offer-booking-cta')
        || root.querySelector('.offer-page__booking > .js-offer-booking-cta');
      const scrollCta = root.querySelector('.js-offer-scroll-rooms')
        || drawer?.querySelector('.js-offer-scroll-rooms')
        || sidebarEl?.querySelector('.js-offer-scroll-rooms');

      if (roomPicked && bookingCta) {
        const href = (bookingCta.getAttribute('href') || '').trim();
        return {
          text: (bookingCta.textContent || '').replace(/\s+/g, ' ').trim() || i18n('book_now', 'Zarezerwuj teraz'),
          href: href && href !== '#' ? href : '#',
          isButton: false,
          pickPackage: false,
        };
      }

      const source = scrollCta;
      if (!source) return { text: ui('Wybierz pokój'), href: `#${ROOMS_SECTION_ID}`, isButton: true, pickPackage: true };
      return {
        text: ui((source.textContent || '').replace(/\s+/g, ' ').trim() || 'Wybierz pokój'),
        href: `#${ROOMS_SECTION_ID}`,
        isButton: true,
        pickPackage: true,
        hidden: Boolean(source.hidden),
      };
    },
    onCtaClick: (event) => {
      const sidebarEl = getOfferSidebar(root);
      const roomPicked = Boolean(
        root.querySelector('.offer-room-card.is-selected')
        || sidebarEl?.classList.contains('is-picked'),
      );
      const bookingCta = sidebarEl?.querySelector('.js-offer-booking-cta:not([hidden])')
        || getOfferDrawer(root)?.querySelector('.js-offer-booking-cta:not([hidden])');
      if (roomPicked && bookingCta) {
        api.closeDrawer?.({ immediate: true });
        return;
      }
      event.preventDefault();
      api.closeDrawer({ immediate: true });
      scrollToAvailableRooms(event);
    },
    onBeforeClose: () => {
      root.querySelectorAll('.js-offer-guests-picker.is-open').forEach((picker) => closeGuestsPicker(picker));
      document.body.querySelectorAll('.js-offer-guests-picker.is-open').forEach((picker) => closeGuestsPicker(picker));
    },
  });

  mobileSidebarSyncers.set(root, api);
};

const syncRoomBookingCta = (root, options = {}) => {
  const shouldQuote = options.quote !== false;
  const selected = syncPickedSidebarUi(root);
  const sidebar = getOfferSidebar(root);
  const hasRoom = Boolean(selected);

  applyOfferSummary(root, null);
  if (hasRoom && !isPricingEnabled(root)) {
    if (selected.dataset.roomPrice) {
      const priceEl = sidebar?.querySelector('.js-offer-price') || root.querySelector('.js-offer-price');
      if (priceEl) priceEl.textContent = selected.dataset.roomPrice;
    }
    const prefix = sidebar?.querySelector('.js-offer-price-prefix') || root.querySelector('.js-offer-price-prefix');
    if (prefix) prefix.hidden = true;
  } else if (!hasRoom) {
    restoreFromPrice(root);
    if (!isPricingEnabled(root)) updatePrice(root);
  }

  updateBookingLinks(root);
  if (shouldQuote && isPricingEnabled(root)) refreshOfferQuote(root);
  mobileSidebarSyncers.get(root)?.sync?.();
};

const initOfferRoomSelect = (root) => {
  if (root.dataset.roomSelectBound === '1') return;
  root.dataset.roomSelectBound = '1';

  root.addEventListener('click', (event) => {
    const button = event.target.closest('.js-offer-select-room');
    if (!(button instanceof HTMLElement) || !root.contains(button)) return;
    if (button.classList.contains('offer-room-card__select--disabled')) return;

    const card = button.closest('.offer-room-card');
    const already = card?.classList.contains('is-selected');

    root.querySelectorAll('.offer-room-card.is-selected').forEach((selected) => {
      selected.classList.remove('is-selected');
      selected.querySelector('.js-offer-select-room')?.setAttribute('aria-pressed', 'false');
    });

    if (!already && card) {
      card.classList.add('is-selected');
      button.setAttribute('aria-pressed', 'true');
    }

    syncRoomBookingCta(root);
  });
};

const ROOMS_SECTION_ID = 'available-rooms';

const scrollToAvailableRooms = (event) => {
  const target = document.getElementById(ROOMS_SECTION_ID);
  if (!target) return;

  event.preventDefault();
  document.querySelectorAll('.js-offer-page').forEach((page) => {
    mobileSidebarSyncers.get(page)?.closeDrawer?.({ immediate: true });
  });

  const fromBody = Number.parseFloat(
    getComputedStyle(document.body).getPropertyValue('--sticky-offset').trim(),
  );
  const sticky = Number.isFinite(fromBody) && fromBody > 0 ? fromBody : 100;
  const switcher = document.querySelector('.js-offer-sub-nav, .offer-sub-nav');
  const switcherH = switcher instanceof HTMLElement ? switcher.getBoundingClientRect().height : 0;
  const top = window.scrollY + target.getBoundingClientRect().top - sticky - switcherH - 12;

  window.scrollTo({
    top: Math.max(0, top),
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
  });

  if (window.history?.replaceState) {
    window.history.replaceState(null, '', `#${ROOMS_SECTION_ID}`);
  }
};

const initOfferRoomsScroll = (root) => {
  root.querySelectorAll('.js-offer-scroll-rooms').forEach((link) => {
    link.setAttribute('href', `#${ROOMS_SECTION_ID}`);
    link.addEventListener('click', scrollToAvailableRooms);
  });
};

const initOfferChangeRoom = (root) => {
  const sidebar = getOfferSidebar(root);
  const links = [
    ...root.querySelectorAll('.js-offer-change-room'),
    ...(sidebar ? [sidebar.querySelector('.js-offer-change-room')] : []),
  ].filter(Boolean);
  links.forEach((link) => {
    if (link.dataset.changeRoomBound === '1') return;
    link.dataset.changeRoomBound = '1';
    link.addEventListener('click', (event) => {
      clearSelectedRooms(root);
      syncRoomBookingCta(root);
      scrollToAvailableRooms(event);
    });
  });
};

const initOfferRoomSliders = (root) => {
  const galleries = [...root.querySelectorAll('.js-offer-room-slider')].filter((gallery) => {
    if (gallery.swiper) return false;
    const slides = gallery.querySelectorAll('.swiper-slide');
    const nav = gallery.querySelector('.offer-room-card__slider-nav');
    if (slides.length < 2) {
      nav?.setAttribute('hidden', '');
      return false;
    }
    return true;
  });
  if (!galleries.length) return;

  // Swiper (~75 KB) poza critical path — pierwsze zdjęcie karty jest już w HTML.
  const boot = () => {
    import('../../../js/swiperWithCss.js').then(({ Swiper, Navigation }) => {
      galleries.forEach((gallery) => {
        if (gallery.swiper) return;
        const prev = gallery.querySelector('.js-offer-room-prev');
        const next = gallery.querySelector('.js-offer-room-next');
        new Swiper(gallery, {
          modules: [Navigation],
          loop: true,
          navigation: {
            prevEl: prev,
            nextEl: next,
          },
        });
      });
    }).catch(() => {});
  };

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(boot, { timeout: 1200 });
  } else {
    window.setTimeout(boot, 1);
  }
};

const TERM_CHEVRON = '<svg class="offer-page__term-chevron" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m5 7.5 5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const renderTermSelectOptions = (wrap) => {
  const select = wrap.querySelector('.js-offer-term');
  const panel = wrap.querySelector('.js-offer-term-panel');
  if (!select || !panel) return;
  panel.replaceChildren();
  [...select.options].forEach((option) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'offer-page__term-option js-offer-term-option';
    btn.setAttribute('role', 'option');
    btn.dataset.value = option.value;
    btn.textContent = option.textContent.trim();
    if (option.selected || option.value === select.value) {
      btn.classList.add('is-selected');
    }
    panel.appendChild(btn);
  });
};

const initOfferTermSelect = (root) => {
  const select = getTermSelect(root);
  if (!select || select.closest('.js-offer-term-select')) return;

  const field = select.closest('.offer-page__field--term') || select.parentElement;
  const nightsEl = field?.querySelector('.js-offer-nights');

  const wrap = document.createElement('div');
  wrap.className = 'offer-page__term-select js-offer-term-select';

  select.classList.add('offer-page__term-native');
  select.setAttribute('tabindex', '-1');
  select.setAttribute('aria-hidden', 'true');

  select.parentNode.insertBefore(wrap, select);
  wrap.appendChild(select);

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'offer-page__term-trigger js-offer-term-trigger';
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-label', 'Termin wyjazdu');

  const calIcon = document.createElement('span');
  calIcon.className = 'offer-page__sum-ic';
  calIcon.setAttribute('aria-hidden', 'true');
  calIcon.innerHTML = ICON_CAL;
  trigger.appendChild(calIcon);

  const valueEl = document.createElement('span');
  valueEl.className = 'offer-page__term-value js-offer-term-value';
  valueEl.textContent = select.selectedOptions[0]?.textContent.trim() || '';
  trigger.appendChild(valueEl);
  if (nightsEl) trigger.appendChild(nightsEl);
  trigger.insertAdjacentHTML('beforeend', TERM_CHEVRON);
  wrap.appendChild(trigger);

  const panel = document.createElement('div');
  panel.className = 'offer-page__term-panel js-offer-term-panel';
  panel.hidden = true;
  panel.setAttribute('role', 'listbox');
  wrap.appendChild(panel);

  renderTermSelectOptions(wrap);

  const open = () => {
    closeGuestsPicker(getOfferSidebar(root)?.querySelector('.js-offer-guests-picker'));
    wrap.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
  };

  trigger.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (wrap.classList.contains('is-open')) {
      closeTermSelect(wrap);
      return;
    }
    open();
  });

  panel.addEventListener('click', (event) => {
    event.stopPropagation();
    const optionBtn = event.target.closest('.js-offer-term-option');
    if (!optionBtn) return;
    select.value = optionBtn.dataset.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    syncTermSelectUi(wrap);
    closeTermSelect(wrap);
  });

  document.addEventListener('click', (event) => {
    if (!wrap.classList.contains('is-open')) return;
    if (wrap.contains(event.target)) return;
    closeTermSelect(wrap);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && wrap.classList.contains('is-open')) {
      closeTermSelect(wrap);
    }
  });
};

const enhanceTermOptions = (root) => {
  const termSelect = getTermSelect(root);
  if (!termSelect) return;
  [...termSelect.options].forEach((option) => {
    if (option.dataset.dateFrom && option.dataset.dateTo) {
      option.textContent = formatTermOptionText(option);
    }
  });
};

/** Stay nights as half-open [from, to) — checkout day exclusive (handover Saturday alone ≠ match). */
const termMatchesSearchDates = (termFrom, termTo, searchFrom, searchTo) => {
  if (!termFrom || !termTo || !searchFrom || !searchTo) return false;
  if (termFrom === searchFrom && termTo === searchTo) return true;
  const tf = new Date(`${termFrom}T12:00:00`).getTime();
  const tt = new Date(`${termTo}T12:00:00`).getTime();
  const sf = new Date(`${searchFrom}T12:00:00`).getTime();
  const st = new Date(`${searchTo}T12:00:00`).getTime();
  if ([tf, tt, sf, st].some((n) => Number.isNaN(n))) return false;
  const overlapStart = Math.max(tf, sf);
  const overlapEnd = Math.min(tt, st);
  const overlapNights = Math.round((overlapEnd - overlapStart) / 86400000);
  return overlapNights >= 1;
};

/** Listing CTA params: query string and/or #hash (SSR redirects QS→hash for WPFC). */
const listingUrlParams = () => {
  const merged = new URLSearchParams(window.location.search);
  const rawHash = (window.location.hash || '').replace(/^#/, '');
  if (!rawHash) return merged;
  const hashQuery = rawHash.startsWith('?') ? rawHash.slice(1) : rawHash;
  // Ignore pure section anchors like #section-szkolenie.
  if (!hashQuery.includes('=')) return merged;
  const fromHash = new URLSearchParams(hashQuery);
  fromHash.forEach((value, key) => {
    if (!merged.has(key)) merged.set(key, value);
  });
  return merged;
};

/**
 * Preselect turnus from listing CTA (?date_from=&date_to= or #…).
 * Never removes/hides options.
 * @returns {boolean} true when selected term differs from SSR default (needs quote refresh).
 */
const hydrateTermFromUrl = (root) => {
  const termSelect = getTermSelect(root);
  if (!termSelect) return false;

  const params = listingUrlParams();
  const dateFrom = params.get('date_from') || '';
  const dateTo = params.get('date_to') || '';
  if (!dateFrom || !dateTo) return false;

  const options = [...termSelect.options];
  const exact = options.find((option) => (
    (option.dataset.isoFrom || '') === dateFrom
    && (option.dataset.isoTo || '') === dateTo
  ));
  const match = exact || options.find((option) => termMatchesSearchDates(
    option.dataset.isoFrom || '',
    option.dataset.isoTo || '',
    dateFrom,
    dateTo,
  ));
  if (!match) return false;

  const prev = termSelect.value;
  if (prev === match.value) return false;

  termSelect.value = match.value;
  syncGuestSelects(root);
  return true;
};

/** Prefer adults/children/ages from listing search query over term defaults. */
const readOccupancyAgesFromRoot = (root) => {
  const picker = getOfferSidebar(root)?.querySelector('.js-offer-guests-picker')
    || root.querySelector('.js-offer-guests-picker');
  const raw = picker?.getAttribute('data-children-ages') || '';
  if (!raw || raw === '[]' || raw === 'null') return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((n) => Number(n)).filter((n) => Number.isFinite(n));
  } catch {
    return [];
  }
};

/** @returns {boolean} true when URL adults/children/ages differ from the SSR card */
const hydrateGuestsFromUrl = (root) => {
  const params = listingUrlParams();
  const adults = params.get('adults');
  const children = params.get('children') ?? params.get('kids');
  if (adults === null && children === null) return false;

  const current = getGuestCounts(root);
  const nextAdults = adults !== null && adults !== ''
    ? Math.max(1, Number(adults) || 1)
    : current.adults;
  const nextChildren = children !== null && children !== ''
    ? Math.max(0, Number(children) || 0)
    : current.children;

  const fromUrl = readChildrenAgesFromParams(params);
  const ages = fromUrl.length ? fromUrl : readOccupancyAgesFromRoot(root);
  const nextAges = [];
  if (nextChildren > 0) {
    nextAges.push(...ages.slice(0, nextChildren));
    while (nextAges.length < nextChildren) nextAges.push(DEFAULT_CHILD_AGE);
  }

  const occupancyChanged = occupancyDiffersFromCard(
    { adults: current.adults, children: current.children, ages: getChildAges(root) },
    { adults: nextAdults, children: nextChildren, ages: nextAges },
  );

  setGuestCounts(root, nextAdults, nextChildren);
  setChildAges(root, nextAges);
  return occupancyChanged;
};

const clearOfferBootLoading = () => {
  if (typeof window.__hskiClearOfferBoot === 'function') {
    window.__hskiClearOfferBoot();
    return;
  }
  document.querySelectorAll('.js-offer-page.is-offer-booting').forEach((root) => {
    root.classList.remove('is-offer-booting');
    root.removeAttribute('aria-busy');
    root.querySelector('.js-offer-boot-loading')?.setAttribute('aria-hidden', 'true');
  });
};

const initOfferPage = () => {
  // SSR już namalował treść — zdejmij overlay zanim ciężkie pickery/slidery.
  // Image skeletons: main.js ładuje imageSkeleton.js osobno (nie wiąż z Offer → Swiper).
  clearOfferBootLoading();

  document.querySelectorAll('.js-offer-page').forEach((root) => {
    upgradeLegacyGuestsSelect(root);
    enhanceTermOptions(root);
    // Listing → #date_from/&date_to: SSR/WPFC paints the first term at default occupancy.
    // Quote when the URL picks another term, or the same first term with a different party.
    const termFromUrlChanged = hydrateTermFromUrl(root);
    const occupancyFromUrlChanged = hydrateGuestsFromUrl(root);
    initOfferGuestsPicker(root);
    initOfferTermSelect(root);
    updateGuestsTriggerLabel(root);

    // Same path as .js-offer-term change: nights/skipass + rooms/prices via quote.
    const refreshAfterTermChange = () => {
      updateTermNightsBadge(root);
      syncTermSelectUi(getTermSelectWrap(root));
      updateGuestsTriggerLabel(root);
      syncRoomBookingCta(root);
    };

    getTermSelect(root)?.addEventListener('change', () => {
      // Nie nadpisuj wyboru gości przy zmianie terminu — tylko cena/noce/CTA.
      refreshAfterTermChange();
    });

    initOfferRoomsScroll(root);
    initOfferChangeRoom(root);
    initOfferRoomSelect(root);
    initOfferRoomSliders(root);
    initOfferSidebarMobile(root);
    rememberFromPrice(root);
    getOfferSidebar(root)?.querySelector('.js-offer-skip-apartment')?.addEventListener('change', () => {
      updateBookingLinks(root);
      updatePrice(root);
    });
    if (getChildAges(root).length === 0) {
      const { children } = getGuestCounts(root);
      if (children > 0) setChildAges(root, Array.from({ length: children }, () => 8));
    }
    root.querySelectorAll('.offer-room-card--unavailable a').forEach((link) => {
      link.addEventListener('click', (event) => event.preventDefault());
    });
    if (shouldQuoteAfterOfferUrlHydrate({
      termChanged: termFromUrlChanged,
      occupancyChanged: occupancyFromUrlChanged,
    })) {
      refreshAfterTermChange();
    } else {
      // SSR już policzył ceny dla pierwszego terminu / plain entry — nie dubluj quote.
      updateTermNightsBadge(root);
      syncTermSelectUi(getTermSelectWrap(root));
      updateGuestsTriggerLabel(root);
      syncRoomBookingCta(root, { quote: false });
    }
    if (!isPricingEnabled(root)) {
      updatePrice(root);
    }
  });
};

export { guestsLabel };
export default initOfferPage;
